import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";
import { AppModule } from "@/enums/AppModule";
import useUserContextData from "../hooks/useUserContextData";
import useUserAssignments from "../hooks/useUserAssignments";
import { parseUserContextData } from "../utils/userContextUtils";
import { IUserContextData } from "../interfaces/UserContext/IUserContextData";
import { User } from "../models/User";
import { clearCookies } from "../utils/utils";

interface AppContextProps {
  children: React.ReactNode;
}

export interface IPageTitle {
  title: string;
  subTitle?: string;
}

export interface IAppContextType {
  userContextToken: string | null;
  userContextData: IUserContextData | null;
  userAssignments: User | null;
  availableModules: AppModule[];
  isSideBarDisable: boolean;
  setIsSideBarDisable: React.Dispatch<React.SetStateAction<boolean>>;
  enableIndexRoute: boolean;
  setEnableIndexRoute: React.Dispatch<React.SetStateAction<boolean>>;
  pageTitle: IPageTitle | null;
  setPageTitle: React.Dispatch<React.SetStateAction<IPageTitle | null>>;
  isDataLoading: boolean;
  isDataReady: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AppContext = createContext<IAppContextType | undefined>(undefined);

const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within a AppContextProvider");
  }
  return context;
};

const AppContextProvider = ({ children }: AppContextProps) => {
  const { instance } = useMsal();
  const {
    data: token,
    isLoading: isTokenLoading,
    isError: isTokenError,
  } = useUserContextData();
  const {
    data: assignments,
    isLoading: isAssignmentsLoading,
    isError: isAssignmentsError,
  } = useUserAssignments(!!token);

  const [userContextToken, setUserContextToken] = useState<string | null>(null);
  const [userContextData, setUserContextData] =
    useState<IUserContextData | null>(null);
  const [userAssignments, setUserAssignments] = useState<User | null>(null);
  const [isSideBarDisable, setIsSideBarDisable] = useState(false);
  const [enableIndexRoute, setEnableIndexRoute] = useState<boolean>(false);
  const [pageTitle, setPageTitle] = useState<IPageTitle | null>(null);

  // Track if data was ever successfully loaded (to prevent logout after successful load)
  const hasEverLoadedSuccessfully = useRef<boolean>(false);
  // Track if auto-logout has been triggered (to prevent multiple logouts)
  const autoLogoutTriggered = useRef<boolean>(false);

  // Calculate loading and ready states
  const isDataLoading = isTokenLoading || (!!token && isAssignmentsLoading);
  const isDataReady =
    !isDataLoading &&
    !isTokenError &&
    !isAssignmentsError &&
    !!token &&
    !!assignments;

  useEffect(() => {
    setUserContextToken(token ?? null);
    setUserContextData(parseUserContextData(token ?? null) ?? null);
  }, [token]);

  useEffect(() => {
    if (assignments) {
      setUserAssignments(assignments);
    }
  }, [assignments]);

  // Track successful data load
  useEffect(() => {
    if (isDataReady) {
      hasEverLoadedSuccessfully.current = true;
    }
  }, [isDataReady]);

  // Auto-logout when custom-token API fails before landing page
  useEffect(() => {
    // Only trigger if:
    // 1. Token error occurred (custom-token API failed)
    // 2. Data was never successfully loaded (before landing page)
    // 3. Not currently loading (error is confirmed)
    // 4. Auto-logout hasn't been triggered yet
    if (
      isTokenError &&
      !hasEverLoadedSuccessfully.current &&
      !isDataLoading &&
      !autoLogoutTriggered.current
    ) {
      autoLogoutTriggered.current = true;

      // Delay logout to give user a chance to see the error message
      const logoutTimeout = setTimeout(() => {
        // Clear session storage and cookies before logout
        sessionStorage.clear();
        clearCookies();

        // Perform logout redirect
        instance.logoutRedirect({
          postLogoutRedirectUri: "/",
        });
      }, 5000); // 5 second delay

      // Cleanup timeout if component unmounts
      return () => {
        clearTimeout(logoutTimeout);
      };
    }
  }, [isTokenError, isDataLoading, instance]);

  const contextValue = useMemo<IAppContextType>(
    () => ({
      userContextToken,
      userContextData,
      userAssignments,
      availableModules: userAssignments?.getAvailableModules() ?? [],
      isSideBarDisable: isSideBarDisable,
      setIsSideBarDisable: setIsSideBarDisable,
      enableIndexRoute: enableIndexRoute,
      setEnableIndexRoute: setEnableIndexRoute,
      pageTitle,
      setPageTitle,
      isDataLoading,
      isDataReady,
    }),
    [
      userContextToken,
      userContextData,
      userAssignments,
      isSideBarDisable,
      enableIndexRoute,
      pageTitle,
      isDataLoading,
      isDataReady,
    ],
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export { AppContextProvider, useAppContext };
